import React, { useEffect, useRef, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, LayersControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { EventResponseDto } from "../services/Dtos/EventsDtos";
import { getEventTypeHexColor } from "../helpers/eventTypeColors";

const { BaseLayer } = LayersControl;

// Fix for default marker icons in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface RadiusSearchMapProps {
  onMapClick?: (lat: number, lng: number) => void;
  radiusSearchCenter?: { lat: number; lng: number; radius: number } | null;
  clickedCoordinates?: { lat: number; lng: number } | null;
  previewRadius?: { lat: number; lng: number; radius: number } | null;
  events?: EventResponseDto[];
}

// Component to handle map clicks
interface MapClickHandlerProps {
  onMapClick: (lat: number, lng: number) => void;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const RadiusSearchMap: React.FC<RadiusSearchMapProps> = ({
  onMapClick,
  radiusSearchCenter,
  clickedCoordinates,
  previewRadius = null,
  events = [],
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const mapInitializedRef = useRef(false);
  const [isEventSummaryExpanded, setIsEventSummaryExpanded] = useState<boolean>(false);

  // Initialize zoom level from localStorage only once on component mount
  const [initialZoom] = useState(() => {
    try {
      const stored = localStorage.getItem("radiusMapZoom");
      const zoomLevel = stored ? parseInt(stored, 10) : 5;
      console.log("[RadiusMap Zoom] Loaded from localStorage:", zoomLevel, "(stored value:", stored, ")");
      return zoomLevel;
    } catch {
      console.log("[RadiusMap Zoom] Failed to read from localStorage, using default: 5");
      return 5;
    }
  });

  const saveZoomToStorage = (zoom: number) => {
    try {
      localStorage.setItem("radiusMapZoom", zoom.toString());
    } catch {
      // Silently fail if localStorage is unavailable
    }
  };

  // Create custom blue icon for radius search center
  const centerIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl:
          "data:image/svg+xml;base64," +
          btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41">
        <path fill="#007bff" stroke="#fff" stroke-width="2" d="M12.5 0C5.596 0 0 5.596 0 12.5c0 8.437 12.5 28.5 12.5 28.5S25 20.937 25 12.5C25 5.596 19.404 0 12.5 0z"/>
        <circle cx="12.5" cy="12.5" r="6" fill="#fff"/>
        <circle cx="12.5" cy="12.5" r="3" fill="#007bff"/>
      </svg>
    `),
        shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      }),
    []
  );

  const previousZoomRef = useRef<number>(10);

  // Filter events that have valid coordinates for addressTo
  const eventsWithCoordinates = useMemo(
    () =>
      events.filter(
        (event) =>
          event.addressTo?.latitude !== undefined &&
          event.addressTo?.longitude !== undefined &&
          !isNaN(event.addressTo.latitude) &&
          !isNaN(event.addressTo.longitude) &&
          event.addressTo.latitude !== 0 &&
          event.addressTo.longitude !== 0
      ),
    [events]
  );

  // Calculate event type counts
  const eventTypeCounts = useMemo(
    () => {
      const counts: { [key: string]: number } = {};
      eventsWithCoordinates.forEach((event) => {
        const eventType = event.eventType;
        counts[eventType] = (counts[eventType] || 0) + 1;
      });
      return counts;
    },
    [eventsWithCoordinates]
  );

  // Create colored icon for event markers based on event type
  const createEventIcon = (eventType: string): L.Icon => {
    const color = getEventTypeHexColor(eventType);
    return new L.Icon({
      iconUrl:
        "data:image/svg+xml;base64," +
        btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41">
        <path fill="${color}" stroke="#fff" stroke-width="2" d="M12.5 0C5.596 0 0 5.596 0 12.5c0 8.437 12.5 28.5 12.5 28.5S25 20.937 25 12.5C25 5.596 19.404 0 12.5 0z"/>
        <circle cx="12.5" cy="12.5" r="6" fill="#fff"/>
        <circle cx="12.5" cy="12.5" r="3" fill="${color}"/>
      </svg>
    `),
        shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      });
  };

  // Master zoom effect for radius search map
  useEffect(() => {
    if (!mapRef.current) return;

    // Priority order for zoom behavior:
    // 1. After search (radiusSearchCenter) - zoom to 10 at search center
    if (radiusSearchCenter) {
      const targetZoom = 10;
      const lat = radiusSearchCenter.lat.toFixed(6);
      const lng = radiusSearchCenter.lng.toFixed(6);
      const radius = radiusSearchCenter.radius;
      console.log(`[RadiusMap ZOOM] SEARCH - Setting zoom=${targetZoom}, lat=${lat}, lng=${lng}, radius=${radius} miles`);

      const timeoutId = setTimeout(() => {
        if (mapRef.current) {
          console.log(`[RadiusMap ZOOM] SEARCH - Executing setView to lat=${lat}, lng=${lng}, zoom=${targetZoom}`);
          mapRef.current.setView(
            [radiusSearchCenter.lat, radiusSearchCenter.lng],
            targetZoom,
            { animate: true, duration: 0.5 }
          );
          console.log(`[RadiusMap ZOOM] SEARCH - setView completed`);
          saveZoomToStorage(targetZoom);
        }
      }, 50);

      previousZoomRef.current = targetZoom;
      mapInitializedRef.current = true;
      return () => clearTimeout(timeoutId);
    }

    // 2. After map click (clickedCoordinates without search) - zoom to 10 at click location
    if (clickedCoordinates && !radiusSearchCenter) {
      const targetZoom = 10;
      const lat = clickedCoordinates.lat.toFixed(6);
      const lng = clickedCoordinates.lng.toFixed(6);
      console.log(`[RadiusMap ZOOM] CLICK - Setting zoom=${targetZoom}, lat=${lat}, lng=${lng}`);

      const timeoutId = setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setView(
            [clickedCoordinates.lat, clickedCoordinates.lng],
            targetZoom,
            { animate: true, duration: 0.5 }
          );
          console.log(`[RadiusMap ZOOM] CLICK - Map centered at lat=${lat}, lng=${lng}`);
          saveZoomToStorage(targetZoom);
          previousZoomRef.current = targetZoom;
          mapInitializedRef.current = true;
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }

    // 3. Clear state - zoom to default USA view
    if (!radiusSearchCenter && !clickedCoordinates && mapInitializedRef.current) {
      const defaultZoom = 5;
      const defaultCenter = [39.8283, -98.5795] as [number, number];
      console.log(`[RadiusMap ZOOM] CLEAR - Resetting to default zoom=${defaultZoom}, center=${defaultCenter}`);

      const timeoutId = setTimeout(() => {
        if (mapRef.current) {
          console.log(`[RadiusMap ZOOM] CLEAR - Executing setView to default center`);
          mapRef.current.setView(defaultCenter, defaultZoom, { animate: true, duration: 0.5 });
          console.log(`[RadiusMap ZOOM] CLEAR - Map reset to default view`);
          saveZoomToStorage(defaultZoom);
          previousZoomRef.current = defaultZoom;
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [clickedCoordinates, radiusSearchCenter]);

  // Effect to track zoom changes and persist to localStorage
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const handleZoomEnd = () => {
      const currentZoom = map.getZoom();
      const center = map.getCenter();
      const lat = center.lat.toFixed(6);
      const lng = center.lng.toFixed(6);
      console.log(`[RadiusMap ZOOM] MANUAL - User zoom ended: zoom=${currentZoom}, lat=${lat}, lng=${lng}`);
      saveZoomToStorage(currentZoom);
      previousZoomRef.current = currentZoom;
    };

    map.on("zoomend", handleZoomEnd);
    return () => {
      map.off("zoomend", handleZoomEnd);
    };
  }, []);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-primary text-white">
        <h5 className="mb-0">Radius Search Map</h5>
      </div>
      <div className="card-body p-0">
        {/* Event summary panel - collapsible at top */}
        <div className="border-bottom">
          <div
            style={{
              padding: "12px 16px",
              cursor: "pointer",
              backgroundColor: "#f8f9fa",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            onClick={() => setIsEventSummaryExpanded(!isEventSummaryExpanded)}
          >
            <h6 className="mb-0 fw-bold" style={{ fontSize: "14px" }}>
              Event Summary
            </h6>
            <span style={{ fontSize: "12px", color: "#666" }}>
              {isEventSummaryExpanded ? "▼" : "▶"}
            </span>
          </div>

          {/* Expanded content */}
          {isEventSummaryExpanded && (
            <div style={{ padding: "16px", backgroundColor: "#fff" }}>
              <div className="mb-3">
                <p className="text-muted mb-2">
                  Total Events: <strong>{eventsWithCoordinates.length}</strong>
                </p>
              </div>

              {/* Event type counts */}
              <div className="event-counts">
                {Object.keys(eventTypeCounts).length > 0 ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "12px" }}>
                    {Object.entries(eventTypeCounts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([eventType, count]) => (
                        <div
                          key={eventType}
                          className="p-2 bg-white rounded border text-center"
                          style={{ borderLeft: `4px solid ${getEventTypeHexColor(eventType)}` }}
                        >
                          <div className="d-flex align-items-center justify-content-center mb-1">
                            <span
                              className="me-1"
                              style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: getEventTypeHexColor(eventType),
                              }}
                            />
                          </div>
                          <small className="text-muted fw-semibold d-block mb-2" style={{ fontSize: "11px" }}>
                            {eventType.replace(/_/g, " ")}
                          </small>
                          <h6 className="mb-0" style={{ fontSize: "18px", fontWeight: "bold" }}>
                            {count}
                          </h6>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-muted text-center mb-0">No events found</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Map container */}
        <div style={{ height: "600px", width: "100%" }}>
          <MapContainer
            center={[39.8283, -98.5795]}
            zoom={initialZoom}
            style={{ height: "100%", width: "100%" }}
            ref={mapRef}
          >
            <LayersControl position="topright">
              <BaseLayer checked name="Street Map">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </BaseLayer>
              <BaseLayer name="Satellite">
                <TileLayer
                  attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                />
              </BaseLayer>
              <BaseLayer name="Terrain">
                <TileLayer
                  attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                  url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                  maxZoom={17}
                />
              </BaseLayer>
            </LayersControl>

            {/* Map click handler for radius search */}
            {onMapClick && <MapClickHandler onMapClick={onMapClick} />}

            {/* Temporary marker when clicked (before search) */}
            {clickedCoordinates && !radiusSearchCenter && (
              <Marker
                position={[clickedCoordinates.lat, clickedCoordinates.lng]}
                icon={centerIcon}
              >
                <Popup>
                  <strong>Selected Location</strong>
                  <br />
                  Lat: {clickedCoordinates.lat.toFixed(6)}
                  <br />
                  Lng: {clickedCoordinates.lng.toFixed(6)}
                  <br />
                  <em>Click "Search Radius" to search this area</em>
                </Popup>
              </Marker>
            )}

            {/* Preview radius circle (when clicking map, before search) */}
            {previewRadius && !radiusSearchCenter && (
              <Circle
                center={[previewRadius.lat, previewRadius.lng]}
                radius={previewRadius.radius * 1609.34}
                pathOptions={{
                  color: "#007bff",
                  fillColor: "#007bff",
                  fillOpacity: 0.05,
                  weight: 2,
                  dashArray: "5, 5",
                }}
              />
            )}

            {/* Radius search center marker and circle (after search) */}
            {radiusSearchCenter && (
              <>
                <Marker
                  position={[radiusSearchCenter.lat, radiusSearchCenter.lng]}
                  icon={centerIcon}
                >
                  <Popup>
                    <strong>Search Center</strong>
                    <br />
                    Lat: {radiusSearchCenter.lat.toFixed(6)}
                    <br />
                    Lng: {radiusSearchCenter.lng.toFixed(6)}
                    <br />
                    Radius: {radiusSearchCenter.radius} miles
                  </Popup>
                </Marker>
                <Circle
                  center={[radiusSearchCenter.lat, radiusSearchCenter.lng]}
                  radius={radiusSearchCenter.radius * 1609.34}
                  pathOptions={{
                    color: "#007bff",
                    fillColor: "#007bff",
                    fillOpacity: 0.1,
                    weight: 2,
                  }}
                />
              </>
            )}

            {/* Event markers from radius search results */}
            {eventsWithCoordinates.map((event) => (
              <Marker
                key={event.id}
                position={[event.addressTo.latitude!, event.addressTo.longitude!]}
                icon={createEventIcon(event.eventType)}
              >
                <Popup>
                  <strong>{event.eventType}</strong>
                  <br />
                  Tracking: {event.trackingNumber}
                  <br />
                  Company: {event.company.name}
                  <br />
                  Address: {event.addressTo.street1}, {event.addressTo.city}, {event.addressTo.state} {event.addressTo.zipCode}
                  <br />
                  Lat: {event.addressTo.latitude?.toFixed(6)}
                  <br />
                  Lng: {event.addressTo.longitude?.toFixed(6)}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default RadiusSearchMap;
